import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formType, setFormType] = useState('contact'); // 'contact' or 'reservation'
  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [reservationData, setReservationData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '2',
    occasion: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactData);
      setSubmitSuccess(true);
      setContactData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReservationSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/reservations`, reservationData);
      setSubmitSuccess(true);
      setReservationData({ name: '', email: '', phone: '', date: '', time: '', guests: '2', occasion: '', message: '' });
    } catch (error) {
      console.error('Reservation error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#FAFAF5]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div
            
            
            
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">Get in Touch</p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-black mb-6">
              Contact <span className="italic">Us</span>
            </h1>
            <p className="text-neutral-600 text-lg">
              Have a question or want to make a reservation? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-12 border-b border-neutral-200">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-testid="contact-info">
            <div
              
              
              className="text-center"
            >
              <MapPin size={24} className="mx-auto mb-4 text-black" />
              <h3 className="font-medium mb-2">Visit Us</h3>
              <p className="text-neutral-600 text-sm">451 High Road<br />Woodford Green, London IG8 0XE</p>
            </div>
            
            <div
              
              
              
              className="text-center"
            >
              <Phone size={24} className="mx-auto mb-4 text-black" />
              <h3 className="font-medium mb-2">Call Us</h3>
              <a href="tel:+447739083131" className="text-neutral-600 text-sm hover:text-black">
                +44 7739 083131
              </a>
            </div>
            
            <div
              
              
              
              className="text-center"
            >
              <Mail size={24} className="mx-auto mb-4 text-black" />
              <h3 className="font-medium mb-2">Email Us</h3>
              <a href="mailto:Enquiries@honeyimhomecoffee.com" className="text-neutral-600 text-sm hover:text-black">
                Enquiries@honeyimhomecoffee.com
              </a>
            </div>
            
            <div
              
              
              
              className="text-center"
            >
              <Clock size={24} className="mx-auto mb-4 text-black" />
              <h3 className="font-medium mb-2">Opening Hours</h3>
              <p className="text-neutral-600 text-sm">
                Tue-Fri: 8am - 5pm<br />
                Sat-Sun: 8:30am - 5pm<br />
                <span className="text-red-600">Mon: Closed (Temporarily)</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          {/* Form Type Toggle */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex border border-neutral-200" data-testid="form-toggle">
              <button
                onClick={() => { setFormType('contact'); setSubmitSuccess(false); }}
                data-testid="contact-form-tab"
                className={`px-8 py-4 text-sm tracking-wider uppercase font-medium transition-all ${
                  formType === 'contact'
                    ? 'bg-black text-white'
                    : 'bg-white text-black hover:bg-neutral-50'
                }`}
              >
                Contact Us
              </button>
              <button
                onClick={() => { setFormType('reservation'); setSubmitSuccess(false); }}
                data-testid="reservation-form-tab"
                className={`px-8 py-4 text-sm tracking-wider uppercase font-medium transition-all ${
                  formType === 'reservation'
                    ? 'bg-black text-white'
                    : 'bg-white text-black hover:bg-neutral-50'
                }`}
              >
                Reserve a Table
              </button>
            </div>
          </div>

          {submitSuccess ? (
            <div
              
              
              className="text-center py-16 bg-[#FAFAF5]"
              data-testid="form-success"
            >
              <CheckCircle size={64} className="mx-auto mb-6 text-black" />
              <h2 className="font-serif text-3xl font-semibold mb-4">Thank You!</h2>
              <p className="text-neutral-600 mb-8">
                {formType === 'contact' 
                  ? 'We\'ve received your message and will get back to you shortly.'
                  : 'Your reservation request has been submitted. We\'ll confirm via email.'}
              </p>
              <button
                onClick={() => setSubmitSuccess(false)}
                className="bg-black text-white px-8 py-4 text-xs tracking-widest uppercase font-bold hover:bg-neutral-800 transition-all"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <div
              key={formType}
              
              
              
            >
              {formType === 'contact' ? (
                <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        value={contactData.name}
                        onChange={(e) => setContactData({...contactData, name: e.target.value})}
                        data-testid="contact-name-input"
                        className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={contactData.email}
                        onChange={(e) => setContactData({...contactData, email: e.target.value})}
                        data-testid="contact-email-input"
                        className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={contactData.phone}
                        onChange={(e) => setContactData({...contactData, phone: e.target.value})}
                        data-testid="contact-phone-input"
                        className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        value={contactData.subject}
                        onChange={(e) => setContactData({...contactData, subject: e.target.value})}
                        data-testid="contact-subject-input"
                        className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                      Message *
                    </label>
                    <textarea
                      value={contactData.message}
                      onChange={(e) => setContactData({...contactData, message: e.target.value})}
                      data-testid="contact-message-input"
                      rows={5}
                      className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none resize-none"
                      required
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="w-full bg-black text-white px-8 py-4 text-xs tracking-widest uppercase font-bold hover:bg-neutral-800 transition-all disabled:opacity-50 flex items-center justify-center"
                  >
                    {isSubmitting ? 'Sending...' : (
                      <>
                        Send Message
                        <Send size={16} className="ml-2" />
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleReservationSubmit} className="space-y-6" data-testid="reservation-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        value={reservationData.name}
                        onChange={(e) => setReservationData({...reservationData, name: e.target.value})}
                        data-testid="res-name-input"
                        className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={reservationData.email}
                        onChange={(e) => setReservationData({...reservationData, email: e.target.value})}
                        data-testid="res-email-input"
                        className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={reservationData.phone}
                        onChange={(e) => setReservationData({...reservationData, phone: e.target.value})}
                        data-testid="res-phone-input"
                        className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                        Number of Guests *
                      </label>
                      <select
                        value={reservationData.guests}
                        onChange={(e) => setReservationData({...reservationData, guests: e.target.value})}
                        data-testid="res-guests-select"
                        className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none"
                        required
                      >
                        {[1,2,3,4,5,6,7,8,9,10].map(n => (
                          <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>
                        ))}
                        <option value="10+">10+ Guests</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                        Preferred Date *
                      </label>
                      <input
                        type="date"
                        value={reservationData.date}
                        onChange={(e) => setReservationData({...reservationData, date: e.target.value})}
                        data-testid="res-date-input"
                        className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                        Preferred Time *
                      </label>
                      <input
                        type="time"
                        value={reservationData.time}
                        onChange={(e) => setReservationData({...reservationData, time: e.target.value})}
                        data-testid="res-time-input"
                        className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                      Occasion
                    </label>
                    <select
                      value={reservationData.occasion}
                      onChange={(e) => setReservationData({...reservationData, occasion: e.target.value})}
                      data-testid="res-occasion-select"
                      className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none"
                    >
                      <option value="">Select an occasion (optional)</option>
                      <option value="birthday">Birthday</option>
                      <option value="anniversary">Anniversary</option>
                      <option value="business">Business Meeting</option>
                      <option value="date">Date Night</option>
                      <option value="celebration">Celebration</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                      Special Requests
                    </label>
                    <textarea
                      value={reservationData.message}
                      onChange={(e) => setReservationData({...reservationData, message: e.target.value})}
                      data-testid="res-message-input"
                      rows={3}
                      placeholder="Any dietary requirements or special requests?"
                      className="w-full bg-transparent border-b border-neutral-300 px-0 py-3 text-black focus:border-black focus:outline-none resize-none placeholder:text-neutral-400"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="res-submit-btn"
                    className="w-full bg-black text-white px-8 py-4 text-xs tracking-widest uppercase font-bold hover:bg-neutral-800 transition-all disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Reservation'}
                  </button>
                  
                  <p className="text-center text-neutral-500 text-xs">
                    Reservations are subject to availability. We'll confirm your booking via email.
                  </p>
                </form>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
